/* eslint-disable @typescript-eslint/no-explicit-any */
import { useFormik } from "formik";
import { trackAssetFormOutcome } from "../../analytics";
import * as Yup from "yup";
import View from "../Container";
import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import Nav, { NavItem } from "../Nav";
import Button from "../Button";
import Text from "../Text";
import { IFindCepResult, useFindCep } from "../../hooks/useFindCep";
import { IAssetInfo } from "../../models/asset";
import moment from "moment";
import { useMutation, useQuery } from "@tanstack/react-query";
import useAuthHttp from "../../hooks/useAuthHttp";
import { IPropertyDetailsResult } from "../../commons/property";
import { ASSET_MIN_PURCHASE_DATE, yupAssetPurchaseDate } from "../../commons/assetPurchaseDate";
import ButtonGroup from "../ButtonGroup";
import FormField from "../FormField";
import Label from "../Label";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputMask } from 'primereact/inputmask';
import { useHomeContext } from "../../contexts/HomeContext";
import Show from "../Show";
import MainLoader from "../../layout/MainLoader";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { InputSwitch } from "primereact/inputswitch";
import { Accordion, AccordionTab } from "primereact/accordion";
import Container from "../Container";
import { IExtractedProperty } from "../../models/extractedData";
import { assetSourceFieldForDirpfImport } from "../../commons/dirpfImportMeta";

const propertyTypes = [
    {
        label: "Apartamento",
        key: 1
    },
    {
        label: "Casa",
        key: 2
    },
    {
        label: "Casa em condomínio",
        key: 3
    },
    {
        label: "Kitnet",
        key: 4
    },
    {
        label: "Sobrado",
        key: 5
    },
    {
        label: "Studio",
        key: 6
    },
    {
        label: "Terreno",
        key: 7
    },
    {
        label: "Lote",
        key: 8
    },
    {
        label: "Sala comercial",
        key: 9
    },
    {
        label: "Edificação",
        key: 10
    },
    {
        label: "Galpão",
        key: 11
    }
];

interface IPropertyHandleFormProps {
    onSuccessRegister(assetInfo: IPropertyHandleForm | null): void;
    goBack(): void;
    assetId?: string;
    initialData?: IExtractedProperty;
}

interface IAssetPropertyUpsert {
    category: "Residencial" | "Comercial";
    type: number;
    purchaseDate: Date;
    zipCode: string;
    city: string;
    state: string;
    place: string;
    district: string;
    number: string;
    complement: string;
    area: number;
    qtyBedroom: number;
    qtyParkingSpaces: number;
    purchaseValue: number;
    alias: string;
    qtyBathroom: number;
    qtySuit: number;
    yearConstruction: string;
    property_andar?: number;
    property_posicao?: string;
    property_insolacao?: string;
    property_vista?: string;
    property_tipologia?: string;
    property_padrao_acabamento?: string;
    property_reforma_recente?: boolean;
    property_condominio_facilidades?: string[];
    property_area_construida_m2?: number;
    property_area_util_m2?: number;
    property_area_terreno_m2?: number;
    property_pavimentos?: number;
    property_suites?: number;
    property_vagas?: number;
    property_facilidades?: string[];
    property_posicao_lote?: string;
    property_face_solar?: string;
    property_ruido?: string;
    property_vista_casa?: string;
    property_condominio_fechado?: boolean;
    property_base_m2_construcao_local?: number;
    property_base_m2_terreno_local?: number;
    property_rua_pavimentada: boolean;
    property_rua_movimentada: boolean;
    property_actual_value: number;
}

interface IPropertyHandleForm extends IFindCepResult {
    groupType?: {
        id?: number;
        name: string;
    };
    type?: {
        id?: number;
        name: string;
    };
    purchaseDate?: Date;
    amount?: number;
    description?: string;
    number?: string;
    complement?: string;
    sizeM2?: number;
    bedrooms?: number;
    suits?: number;
    bathrooms?: number;
    garages?: number;
    yearConstruction?: string;
    property_andar?: number;
    property_posicao?: { id: PropertyPosition; label: string };
    property_insolacao?: { id: PropertyInsolation; label: string };
    property_vista?: { id: PropertyVista; label: string };
    property_tipologia?: { id: PropertyTipology; label: string };
    property_padrao_acabamento?: { id: PropertyStandardFinishing; label: string };
    property_reforma_recente?: boolean;
    property_condominio_facilidades?: { id: PropertyCondominioFacilities; label: string }[];
    property_area_construida_m2?: number;
    property_area_util_m2?: number;
    property_area_terreno_m2?: number;
    property_pavimentos?: number;
    property_suites?: number;
    property_vagas?: number;
    property_facilidades?: { id: PropertyFacility; label: string }[];
    property_posicao_lote?: { id: PropertyPositionLote; label: string };
    property_face_solar?: { id: PropertyFaceSolar; label: string };
    property_ruido?: { id: PropertyNoise; label: string };
    property_vista_casa?: { id: PropertyVistaCasa; label: string };
    property_condominio_fechado?: boolean;
    property_base_m2_construcao_local?: number;
    property_base_m2_terreno_local?: number;
    property_rua_pavimentada?: boolean;
    property_rua_movimentada?: boolean;
    property_actual_value?: number;
}

enum PropertyFacility {
    Piscina = "piscina",
    Edicula = "edicula",
    Gourmet = "gourmet",
    Escritorio = "escritorio",
    Lareira = "lareira",
    Solar = "solar",
    Cisterna = "cisterna",
    Cftv = "cftv",
    PortaoAuto = "portao_auto"
}

enum PropertyPositionLote {
    MeioQuadra = "meio_quadra",
    Esquina = "esquina",
    CulDeSac = "cul_de_sac"
}

enum PropertyFaceSolar {
    FrenteNorte = "frente_norte",
    FrenteSul = "frente_sul",
    Leste = "leste",
    Oeste = "oeste"
}

enum PropertyNoise {
    Baixo = "baixo",
    Medio = "medio",
    Alto = "alto"
}

enum PropertyVistaCasa {
    Nenhuma = "nenhuma",
    Parcial = "parcial",
    Aberta = "aberta"
}

enum PropertyPosition {
    Frente = "frente",
    Lateral = "lateral",
    Fundos = "fundos"
}

enum PropertyInsolation {
    Manha = "manha",
    Tarde = "tarde",
    Mista = "mista",
    SemLuzDireta = "sem_luz_direta"
}

enum PropertyVista {
    Nenhuma = "nenhuma",
    Parcial = "parcial",
    Definitiva = "definitiva"
}

enum PropertyTipology {
    Padrao = "padrao",
    Studio = "studio",
    Garden = "garden",
    Duplex = "duplex",
    Cobertura = "cobertura"
}

enum PropertyStandardFinishing {
    Baixo = "baixo",
    Medio = "medio",
    Alto = "alto",
    Luxo = "luxo"
}

enum PropertyCondominioFacilities {
    Piscina = "piscina",
    Academia = "academia",
    Coworking = "coworking",
    Seguranca24h = "seguranca24h",
    Rooftop = "rooftop",
    Playground = "playground",
    Quadra = "quadra",
    Petplace = "petplace"
}

const PropertyHandleForm = (props: IPropertyHandleFormProps) => {
    const toast = useRef<Toast | null>(null);
    const http = useAuthHttp();
    const [isEdit, setIsEdit] = useState(false);
    const homeContext = useHomeContext();

    const propertDetailsQuery = useQuery({
        queryKey: ["propertyDetails", props.assetId],
        gcTime: 0,
        queryFn: async () => {
            if (!props.assetId) {
                setIsEdit(false);
                return null;
            }

            setIsEdit(true);

            return await http.doGet<IPropertyDetailsResult>(`/property/user/${props.assetId}`)
        }
    });

    const propertyInsertMutation = useMutation({
        mutationFn: async (form: IAssetPropertyUpsert) => {
            const body = {
                property_category: form.category == "Residencial" ? 1 : 2,
                property_type: form.type,
                property_purchase_date: moment(form.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                property_zip_code: form.zipCode,
                property_city: form.city,
                property_state: form.state,
                property_place: form.place,
                property_district: form.district,
                property_number: form.number,
                property_complement: form.complement,
                property_area: form.area,
                property_qtd_bedroom: form.qtyBedroom,
                property_qtd_parking_spaces: form.qtyParkingSpaces,
                property_purchase_value: form.purchaseValue,
                property_alias: form.alias,
                property_qtd_bathroom: form.qtyBathroom,
                property_qtd_suit: form.qtySuit,
                property_construction_year: form.yearConstruction,
                property_andar: form.property_andar,
                property_posicao: form.property_posicao,
                property_insolacao: form.property_insolacao,
                property_vista: form.property_vista,
                property_tipologia: form.property_tipologia,
                property_padrao_acabamento: form.property_padrao_acabamento,
                property_reforma_recente: form.property_reforma_recente,
                property_condominio_facilidades: form.property_condominio_facilidades,
                property_area_construida_m2: form.property_area_construida_m2,
                property_area_util_m2: form.property_area_util_m2,
                property_area_terreno_m2: form.property_area_terreno_m2,
                property_pavimentos: form.property_pavimentos,
                property_suites: form.property_suites,
                property_vagas: form.property_vagas,
                property_facilidades: form.property_facilidades,
                property_posicao_lote: form.property_posicao_lote,
                property_face_solar: form.property_face_solar,
                property_ruido: form.property_ruido,
                property_vista_casa: form.property_vista_casa,
                property_condominio_fechado: form.property_condominio_fechado,
                property_base_m2_construcao_local: form.property_base_m2_construcao_local,
                property_base_m2_terreno_local: form.property_base_m2_terreno_local,
                property_rua_pavimentada: form.property_rua_pavimentada,
                property_rua_movimentada: form.property_rua_movimentada,
                property_actual_value: form.property_actual_value,
                ...(assetSourceFieldForDirpfImport(props.initialData) ?? {}),
            };

            return await http.doPost<any, IAssetInfo>("/property/user/insert", body);
        }
    });

    const propertyUpdateMutation = useMutation({
        mutationFn: async (form: IAssetPropertyUpsert) => {
            const body = {
                property_category: form.category == "Residencial" ? 1 : 2,
                property_type: form.type,
                property_purchase_date: moment(form.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                property_zip_code: form.zipCode,
                property_city: form.city,
                property_state: form.state,
                property_place: form.place,
                property_district: form.district,
                property_number: form.number,
                property_complement: form.complement,
                property_area: form.area,
                property_qtd_bedroom: form.qtyBedroom,
                property_qtd_parking_spaces: form.qtyParkingSpaces,
                property_qtd_bathroom: form.qtyBathroom,
                property_qtd_suit: form.qtySuit,
                property_construction_year: form.yearConstruction,
                property_purchase_value: form.purchaseValue,
                property_alias: form.alias,
                property_andar: form.property_andar,
                property_posicao: form.property_posicao,
                property_insolacao: form.property_insolacao,
                property_vista: form.property_vista,
                property_tipologia: form.property_tipologia,
                property_padrao_acabamento: form.property_padrao_acabamento,
                property_reforma_recente: form.property_reforma_recente,
                property_condominio_facilidades: form.property_condominio_facilidades,
                property_area_construida_m2: form.property_area_construida_m2,
                property_area_util_m2: form.property_area_util_m2,
                property_area_terreno_m2: form.property_area_terreno_m2,
                property_pavimentos: form.property_pavimentos,
                property_suites: form.property_suites,
                property_vagas: form.property_vagas,
                property_facilidades: form.property_facilidades,
                property_posicao_lote: form.property_posicao_lote,
                property_face_solar: form.property_face_solar,
                property_ruido: form.property_ruido,
                property_vista_casa: form.property_vista_casa,
                property_condominio_fechado: form.property_condominio_fechado,
                property_base_m2_construcao_local: form.property_base_m2_construcao_local,
                property_base_m2_terreno_local: form.property_base_m2_terreno_local,
                property_rua_pavimentada: form.property_rua_pavimentada,
                property_rua_movimentada: form.property_rua_movimentada,
                property_actual_value: form.property_actual_value,
            };

            return await http.doPatch<any, IAssetInfo>(`/property/user/${props.assetId}`, body);
        }
    });

    // Função helper para criar valores iniciais baseados nos dados da query
    const getInitialValues = (): IPropertyHandleForm => {
        const data = propertDetailsQuery?.data?.property_user;
        
        return {
            groupType: data?.property_category ? {
                id: data.property_category,
                name: data.property_category == 1 ? "Residencial" : "Comercial"
            } : undefined,
            type: data?.property_type ? {
                id: data.property_type,
                name: propertyTypes.find(p => p.key == data.property_type)?.label ?? ""
            } : undefined,
            purchaseDate: data?.property_purchase_date ? new Date(data.property_purchase_date) : undefined,
            amount: data?.property_purchase_value,
            description: data?.property_alias,
            cep: data?.property_zip_code,
            logradouro: data?.property_place,
            bairro: data?.property_district,
            localidade: data?.property_city,
            uf: data?.property_state,
            number: data?.property_number,
            complement: data?.property_complement,
            sizeM2: data?.property_area,
            yearConstruction: data?.property_construction_year,
            bedrooms: data?.property_qtd_bedroom,
            bathrooms: data?.property_qtd_bathroom,
            garages: data?.property_qtd_parking_spaces,
            suits: data?.property_qtd_suit,
            property_andar: data?.property_andar,
            property_posicao: data?.property_posicao ? {
                id: data.property_posicao as PropertyPosition,
                label: data.property_posicao
            } : undefined,
            property_insolacao: data?.property_insolacao ? {
                id: data.property_insolacao as PropertyInsolation,
                label: data.property_insolacao
            } : undefined,
            property_vista: data?.property_vista ? {
                id: data.property_vista as PropertyVista,
                label: data.property_vista
            } : undefined,
            property_tipologia: data?.property_tipologia ? {
                id: data.property_tipologia as PropertyTipology,
                label: data.property_tipologia
            } : undefined,
            property_padrao_acabamento: data?.property_padrao_acabamento ? {
                id: data.property_padrao_acabamento as PropertyStandardFinishing,
                label: data.property_padrao_acabamento
            } : undefined,
            property_reforma_recente: data?.property_reforma_recente,
            property_condominio_facilidades: data?.property_condominio_facilidades?.map(f => ({
                id: f as PropertyCondominioFacilities,
                label: f
            })),
            property_area_construida_m2: data?.property_area_construida_m2,
            property_area_util_m2: data?.property_area_util_m2,
            property_area_terreno_m2: data?.property_area_terreno_m2,
            property_pavimentos: data?.property_pavimentos,
            property_suites: data?.property_suites,
            property_vagas: data?.property_vagas,
            property_facilidades: data?.property_facilidades?.filter(f => f).map(f => ({
                id: f as PropertyFacility,
                label: f
            })),
            property_posicao_lote: data?.property_posicao_lote ? {
                id: data.property_posicao_lote as PropertyPositionLote,
                label: data.property_posicao_lote
            } : undefined,
            property_face_solar: data?.property_face_solar ? {
                id: data.property_face_solar as PropertyFaceSolar,
                label: data.property_face_solar
            } : undefined,
            property_ruido: data?.property_ruido ? {
                id: data.property_ruido as PropertyNoise,
                label: data.property_ruido
            } : undefined,
            property_vista_casa: data?.property_vista_casa ? {
                id: data.property_vista_casa as PropertyVistaCasa,
                label: data.property_vista_casa
            } : undefined,
            property_condominio_fechado: data?.property_condominio_fechado,
            property_base_m2_construcao_local: data?.property_base_m2_construcao_local,
            property_base_m2_terreno_local: data?.property_base_m2_terreno_local,
            property_rua_pavimentada: data?.property_rua_pavimentada,
            property_rua_movimentada: data?.property_rua_movimentada,
            property_actual_value: data?.property_actual_value,
        };
    };

    const formik = useFormik<IPropertyHandleForm>({
        enableReinitialize: false,
        initialValues: getInitialValues(),
        validationSchema: Yup.object().shape({
            groupType: Yup
                .object()
                .required("Campo obrigatório"),
            type: Yup
                .object()
                .required("Campo obrigatório"),
            purchaseDate: yupAssetPurchaseDate(),
            amount: Yup
                .number()
                .min(1, "É necessário informar um valor")
                .required("Campo obrigatório"),
            description: Yup.string().required("Campo obrigatório"),
            cep: Yup.string().required("Campo obrigatório"),
            logradouro: Yup.string().required("Campo obrigatório"),
            bairro: Yup.string().required("Campo obrigatório"),
            localidade: Yup.string().required("Campo obrigatório"),
            uf: Yup.string().required("Campo obrigatório"),
            number: Yup.string().required("Campo obrigatório"),
            complement: Yup.string().nullable().optional(),
            sizeM2: Yup.string().required("Campo obrigatório"),
            yearConstruction: Yup
                .string()
                .when(
                    "type",
                    ([type], schema) => {
                        if (!type) {
                            return schema;
                        }

                        if (type.name != "Terreno") {
                            return schema.required("Campo obrigatório");
                        }

                        return schema;
                    }
                ),
            property_andar: Yup.number().optional().nullable(),
            property_posicao: Yup.object().optional().nullable(),
            property_insolacao: Yup.object().optional().nullable(),
            property_vista: Yup.object().optional().nullable(),
            property_tipologia: Yup.object().optional().nullable(),
            property_padrao_acabamento: Yup.object().optional().nullable(),
            property_reforma_recente: Yup.boolean().optional().nullable(),
            property_condominio_facilidades: Yup.array().of(Yup.string().optional().nullable()).optional().nullable(),
            property_area_construida_m2: Yup.number().optional().nullable(),
            property_area_util_m2: Yup.number().optional().nullable(),
            property_area_terreno_m2: Yup.number().optional().nullable(),
            property_pavimentos: Yup.number().optional().nullable(),
            property_suites: Yup.number().optional().nullable(),
            property_vagas: Yup.number().optional().nullable(),
            property_facilidades: Yup.array().of(Yup.string().optional().nullable()).optional().nullable(),
            property_posicao_lote: Yup.object().optional().nullable(),
            property_face_solar: Yup.object().optional().nullable(),
            property_ruido: Yup.object().optional().nullable(),
            property_vista_casa: Yup.object().optional().nullable(),
            property_condominio_fechado: Yup.boolean().optional().nullable(),
            property_base_m2_construcao_local: Yup.number().optional().nullable(),
            property_base_m2_terreno_local: Yup.number().optional().nullable(),
            property_rua_pavimentada: Yup.boolean().optional().nullable(),
            property_rua_movimentada: Yup.boolean().optional().nullable(),
            property_actual_value: Yup.number().min(1, "É necessário informar um valor").required("Campo obrigatório"),
        }),
        onSubmit: async (form: IPropertyHandleForm) => {
            await send(form);
        }
    });

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    // Atualizar todos os campos quando os dados chegarem usando setValues
    useEffect(() => {
        if (!propertDetailsQuery?.data?.property_user || propertDetailsQuery.isLoading) {
            return;
        }
        
        // Usar setValues para atualizar todos os valores de uma vez, similar ao DebentureHandleForm
        const initialValues = getInitialValues();
        // Garantir que setValues seja chamado de forma assíncrona para evitar problemas de timing
        setTimeout(() => {
            formik.setValues(initialValues);
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertDetailsQuery?.data?.property_user, propertDetailsQuery?.isLoading]);

    // Preencher formulário com dados importados
    useEffect(() => {
        if (!props.initialData) {
            return;
        }

        const data = props.initialData;
        const categoryId = data.property_category === 1 ? 1 : 2;
        const category = categoryId === 1 ? "Residencial" : "Comercial";
        const propertyType = propertyTypes.find(p => p.key === data.property_type);

        formik.setValues({
            groupType: {
                id: categoryId,
                name: category
            },
            type: propertyType ? {
                id: propertyType.key,
                name: propertyType.label
            } : undefined,
            purchaseDate: data.property_purchase_date ? moment(data.property_purchase_date).toDate() : undefined,
            amount: data.property_purchase_value || 0,
            description: data.property_alias || "",
            cep: data.property_zip_code || "",
            logradouro: data.property_place || "",
            bairro: data.property_district || "",
            localidade: data.property_city || "",
            uf: data.property_state || "",
            number: data.property_number || "",
            complement: data.property_complement || "",
            sizeM2: data.property_area || 0,
            bedrooms: data.property_qtd_bedroom || 0,
            bathrooms: data.property_qtd_bathroom || 0,
            garages: data.property_qtd_parking_spaces || 0,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialData]);

    const cepQuery = useFindCep();
    useEffect(() => {
        const timeout = setTimeout(async () => {
            if ((formik.values.cep?.length ?? 0) < 8 || !formik.values.cep) {
                return; 
            }

            const cep = await cepQuery.findCep(formik.values.cep.replace("-", ""));
            
            if (cep) {
                formik.setFieldValue("cep", cep.cep);
                formik.setFieldValue("logradouro", cep.logradouro);
                formik.setFieldValue("bairro", cep.bairro);
                formik.setFieldValue("localidade", cep.localidade);
                formik.setFieldValue("uf", cep.uf);
            }
        }, 1500);

        return () => clearTimeout(timeout);
    }, [formik.values.cep]);

    const send = async (form: IPropertyHandleForm) => {
        const assetPayload = {
            category: form.groupType!.name as "Residencial" | "Comercial",
            type: form.type!.id,
            purchaseDate: form.purchaseDate,
            zipCode: form.cep,
            city: form.localidade,
            state: form.uf,
            place: form.logradouro,
            district: form.bairro,
            number: form.number,
            complement: form.complement,
            area: form.sizeM2,
            qtyBedroom: form.bedrooms,
            qtyParkingSpaces: form.garages,
            purchaseValue: form.amount,
            alias: form.description,
            qtyBathroom: form.bathrooms,
            qtySuit: form.suits,
            yearConstruction: form.yearConstruction,
            property_andar: form.property_andar,
            property_posicao: form.property_posicao?.id,
            property_insolacao: form.property_insolacao?.id,
            property_vista: form.property_vista?.id,
            property_tipologia: form.property_tipologia?.id,
            property_padrao_acabamento: form.property_padrao_acabamento?.id,
            property_reforma_recente: form.property_reforma_recente,
            property_condominio_facilidades: form.property_condominio_facilidades,
            property_area_construida_m2: form.property_area_construida_m2,
            property_area_util_m2: form.property_area_util_m2,
            property_area_terreno_m2: form.property_area_terreno_m2,
            property_pavimentos: form.property_pavimentos,
            property_suites: form.property_suites,
            property_vagas: form.property_vagas,
            property_facilidades: form.property_facilidades,
            property_posicao_lote: form.property_posicao_lote?.id,
            property_face_solar: form.property_face_solar?.id,
            property_ruido: form.property_ruido?.id,
            property_vista_casa: form.property_vista_casa?.id,
            property_condominio_fechado: form.property_condominio_fechado,
            property_base_m2_construcao_local: form.property_base_m2_construcao_local,
            property_base_m2_terreno_local: form.property_base_m2_terreno_local,
            property_rua_pavimentada: form.property_rua_pavimentada,
            property_rua_movimentada: form.property_rua_movimentada,
            property_actual_value: form.property_actual_value,
        } as IAssetPropertyUpsert;

        if (!assetPayload) {
            return;
        }

        try {
            let assetInfo;
            if (!props.assetId) {
                assetInfo = await propertyInsertMutation.mutateAsync(assetPayload);
            } else {
                assetInfo = await propertyUpdateMutation.mutateAsync(assetPayload);
            }

            if (assetInfo) {
                props.onSuccessRegister(form);
                await homeContext.refresh();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao salvar investimento"
                });
            }
        } catch (err) {
            if (!isEdit) trackAssetFormOutcome('property', false, 'failed');
            toast.current?.show({
                severity: "error",
                summary: (err as Error).message
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Show when={propertDetailsQuery.isLoading}>
                <Container center fullWidth>
                    <MainLoader fullHeight={false} minHeight="300px" />
                </Container>
            </Show>

            <Show when={!propertDetailsQuery.isLoading}>
                <View flexColumn gap={3}>
                    <h2 className="m-0">Imóvel</h2>

                    <Nav>
                        <NavItem
                            id="go-back"
                            showPrefixIcon
                            onSelectOption={() => props.goBack()}
                        >
                            Voltar
                        </NavItem>
                    </Nav>

                    <Container className="p-accordion-property-form" fullWidth>
                    <Accordion multiple activeIndex={[0]}>
                        <AccordionTab header="Tipo do imóvel">
                    <View flexColumn gap={2} className="pt-2">
                    <View flexColumn>
                        <Label>
                            Qual a categoria?
                        </Label>

                        <View
                            className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded`}
                        >
                            <ButtonGroup
                                invalid={!!formik.errors.groupType && formik.touched.groupType}
                                group={
                                    [
                                        {
                                            id: 1,
                                            name: "Residencial"
                                        },
                                        {
                                            id: 2,
                                            name: "Comercial"
                                        }
                                    ]
                                        .map(g => (
                                            {
                                                name: g.name,
                                                disabled: isEdit,
                                                selected: formik.values.groupType?.id == g.id,
                                                onClick: () => formik.setFieldValue("groupType", g)
                                            })) ?? []
                                }
                            />
                        </View>

                        {
                            !!formik.errors.groupType && formik.touched.groupType && (
                                <Text className="text-red-500 mt-2" size="xs">{formik.errors.groupType}</Text>
                            )
                        }
                    </View>

                    <View flexColumn>
                        <Label>
                            Escolha um tipo
                        </Label>

                        <View
                            className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded`}
                        >
                            <ButtonGroup
                                invalid={!!formik.errors.type && formik.touched.type}
                                group={
                                    propertyTypes
                                        .map(g => (
                                            {
                                                name: g.label,
                                                disabled: isEdit,
                                                selected: formik.values.type?.id == g.key,
                                                onClick: () => {
                                                    const category = formik.values.groupType;
                                                    formik.resetForm();
                                                    formik.setFieldValue("type", { id: g.key, name: g.label });
                                                    formik.setFieldValue("groupType", category);
                                                }
                                            })) ?? []
                                }
                            />
                        </View>

                        {
                            !!formik.errors.type && formik.touched.type && (
                                <Text className="text-red-500 mt-2" size="xs">{formik.errors.type}</Text>
                            )
                        }
                    </View>
                    </View>
                        </AccordionTab>

                        {(formik.values.type?.name === "Apartamento" || formik.values.type?.name === "Studio" || formik.values.type?.name === "Kitnet") && (
                        <AccordionTab header="Detalhes do imóvel">
                    <View flexColumn gap={2} className="pt-2">
                        <FormField
                            id="property_andar"
                            label="Andar"
                            hasError={!!formik.errors.property_andar && formik.touched.property_andar}
                            error={formik.errors.property_andar}
                        >
                            <InputNumber
                                invalid={!!formik.errors.property_andar && formik.touched.property_andar}
                                value={formik.values.property_andar}
                                minFractionDigits={0}
                                onValueChange={(e) => formik.setFieldValue("property_andar", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_posicao"
                            label="Posição"
                            hasError={!!formik.errors.property_posicao && formik.touched.property_posicao}
                            error={formik.errors.property_posicao}
                        >
                            <Dropdown
                                value={formik.values.property_posicao}
                                onChange={(e) => formik.setFieldValue("property_posicao", e.value)}
                                options={[{ id: PropertyPosition.Frente, label: "Frente" }, { id: PropertyPosition.Lateral, label: "Lateral" }, { id: PropertyPosition.Fundos, label: "Fundos" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_posicao && formik.touched.property_posicao}
                            />
                        </FormField>

                        <FormField
                            id="property_insolacao"
                            label="Insolação"
                            hasError={!!formik.errors.property_insolacao && formik.touched.property_insolacao}
                            error={formik.errors.property_insolacao}
                        >
                            <Dropdown
                                value={formik.values.property_insolacao}
                                onChange={(e) => formik.setFieldValue("property_insolacao", e.value)}
                                options={[{ id: PropertyInsolation.Manha, label: "Manhã" }, { id: PropertyInsolation.Tarde, label: "Tarde" }, { id: PropertyInsolation.Mista, label: "Mista" }, { id: PropertyInsolation.SemLuzDireta, label: "Sem luz direta" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_insolacao && formik.touched.property_insolacao}
                            />
                        </FormField>

                        <FormField
                            id="property_vista"
                            label="Vista"
                            hasError={!!formik.errors.property_vista && formik.touched.property_vista}
                            error={formik.errors.property_vista}
                        >
                            <Dropdown
                                value={formik.values.property_vista}
                                onChange={(e) => formik.setFieldValue("property_vista", e.value)}
                                options={[{ id: PropertyVista.Nenhuma, label: "Nenhuma" }, { id: PropertyVista.Parcial, label: "Parcial" }, { id: PropertyVista.Definitiva, label: "Definitiva" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_vista && formik.touched.property_vista}
                            />
                        </FormField>

                        <FormField
                            id="property_tipologia"
                            label="Tipologia"
                            hasError={!!formik.errors.property_tipologia && formik.touched.property_tipologia}
                            error={formik.errors.property_tipologia}
                        >
                            <Dropdown
                                value={formik.values.property_tipologia}
                                onChange={(e) => formik.setFieldValue("property_tipologia", e.value)}
                                options={[{ id: PropertyTipology.Padrao, label: "Padrão" }, { id: PropertyTipology.Studio, label: "Studio" }, { id: PropertyTipology.Garden, label: "Garden" }, { id: PropertyTipology.Duplex, label: "Duplex" }, { id: PropertyTipology.Cobertura, label: "Cobertura" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_tipologia && formik.touched.property_tipologia}
                            />
                        </FormField>

                        <FormField
                            id="property_condominio_facilidades"
                            label="Facilidades do condomínio"
                            hasError={!!formik.errors.property_condominio_facilidades && formik.touched.property_condominio_facilidades}
                            error={formik.errors.property_condominio_facilidades}
                        >
                            <MultiSelect
                                value={formik.values.property_condominio_facilidades ?? []}
                                onChange={(e) => formik.setFieldValue("property_condominio_facilidades", e.value)}
                                options={[{ id: PropertyCondominioFacilities.Piscina, label: "Piscina" }, { id: PropertyCondominioFacilities.Academia, label: "Academia" }, { id: PropertyCondominioFacilities.Coworking, label: "Coworking" }, { id: PropertyCondominioFacilities.Seguranca24h, label: "Segurança 24h" }, { id: PropertyCondominioFacilities.Rooftop, label: "Rooftop" }, { id: PropertyCondominioFacilities.Playground, label: "Playground" }, { id: PropertyCondominioFacilities.Quadra, label: "Quadra" }, { id: PropertyCondominioFacilities.Petplace, label: "Petplace" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_condominio_facilidades && formik.touched.property_condominio_facilidades}
                            />
                        </FormField>
                    </View>
                        </AccordionTab>
                        )}

                        {(formik.values.type?.name === "Casa"
                            || formik.values.type?.name === "Casa em condomínio"
                            || formik.values.type?.name === "Sobrado"
                            || formik.values.type?.name === "Apartamento"
                            || formik.values.type?.name === "Studio"
                            || formik.values.type?.name === "Kitnet") && (
                        <AccordionTab header="Características">
                    <View flexColumn gap={2} className="pt-2">
                        <FormField
                            id="property_padrao_acabamento"
                            label="Padrão de acabamento"
                            hasError={!!formik.errors.property_padrao_acabamento && formik.touched.property_padrao_acabamento}
                            error={formik.errors.property_padrao_acabamento}
                        >
                            <Dropdown
                                value={formik.values.property_padrao_acabamento}
                                onChange={(e) => formik.setFieldValue("property_padrao_acabamento", e.value)}
                                options={[{ id: PropertyStandardFinishing.Baixo, label: "Baixo" }, { id: PropertyStandardFinishing.Medio, label: "Médio" }, { id: PropertyStandardFinishing.Alto, label: "Alto" }, { id: PropertyStandardFinishing.Luxo, label: "Luxo" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_padrao_acabamento && formik.touched.property_padrao_acabamento}
                            />
                        </FormField>

                        <FormField
                            id="property_reforma_recente"
                            label="Reformas recentes"
                            hasError={!!formik.errors.property_reforma_recente && formik.touched.property_reforma_recente}
                            error={formik.errors.property_reforma_recente}
                        >
                            <InputSwitch
                                checked={formik.values.property_reforma_recente ?? false}
                                onChange={(e) => formik.setFieldValue("property_reforma_recente", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_rua_pavimentada"
                            label="Rua pavimentada"
                            hasError={!!formik.errors.property_rua_pavimentada && formik.touched.property_rua_pavimentada}
                            error={formik.errors.property_rua_pavimentada}
                        >
                            <InputSwitch
                                checked={formik.values.property_rua_pavimentada ?? false}
                                onChange={(e) => formik.setFieldValue("property_rua_pavimentada", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_rua_movimentada"
                            label="Rua movimentada"
                            hasError={!!formik.errors.property_rua_movimentada && formik.touched.property_rua_movimentada}
                            error={formik.errors.property_rua_movimentada}
                        >
                            <InputSwitch
                                checked={formik.values.property_rua_movimentada ?? false}
                                onChange={(e) => formik.setFieldValue("property_rua_movimentada", e.value)}
                            />
                        </FormField>
                    </View>
                        </AccordionTab>
                        )}

                        {(formik.values.type?.name === "Casa" || formik.values.type?.name === "Casa em condomínio" || formik.values.type?.name === "Sobrado") && (
                        <AccordionTab header="Área e estrutura">
                    <View flexColumn gap={2} className="pt-2">
                        <FormField
                            id="property_area_construida_m2"
                            label="Área construída total"
                            hasError={!!formik.errors.property_area_construida_m2 && formik.touched.property_area_construida_m2}
                            error={formik.errors.property_area_construida_m2}
                        >
                            <InputNumber
                                invalid={!!formik.errors.property_area_construida_m2 && formik.touched.property_area_construida_m2}
                                value={formik.values.property_area_construida_m2}
                                minFractionDigits={2}
                                onValueChange={(e) => formik.setFieldValue("property_area_construida_m2", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_area_util_m2"
                            label="Área útil interna"
                            hasError={!!formik.errors.property_area_util_m2 && formik.touched.property_area_util_m2}
                            error={formik.errors.property_area_util_m2}
                        >
                            <InputNumber
                                invalid={!!formik.errors.property_area_util_m2 && formik.touched.property_area_util_m2}
                                value={formik.values.property_area_util_m2}
                                minFractionDigits={2}
                                onValueChange={(e) => formik.setFieldValue("property_area_util_m2", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_area_terreno_m2"
                            label="Área total do terreno"
                            hasError={!!formik.errors.property_area_terreno_m2 && formik.touched.property_area_terreno_m2}
                            error={formik.errors.property_area_terreno_m2}
                        >
                            <InputNumber
                                invalid={!!formik.errors.property_area_terreno_m2 && formik.touched.property_area_terreno_m2}
                                value={formik.values.property_area_terreno_m2}
                                minFractionDigits={2}
                                onValueChange={(e) => formik.setFieldValue("property_area_terreno_m2", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_pavimentos"
                            label="Número de pavimentos"
                            hasError={!!formik.errors.property_pavimentos && formik.touched.property_pavimentos}
                            error={formik.errors.property_pavimentos}
                        >
                            <InputNumber
                                invalid={!!formik.errors.property_pavimentos && formik.touched.property_pavimentos}
                                value={formik.values.property_pavimentos}
                                minFractionDigits={0}
                                onValueChange={(e) => formik.setFieldValue("property_pavimentos", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_suites"
                            label="Número de suítes"
                            hasError={!!formik.errors.property_suites && formik.touched.property_suites}
                            error={formik.errors.property_suites}
                        >
                            <InputNumber
                                invalid={!!formik.errors.property_suites && formik.touched.property_suites}
                                value={formik.values.property_suites}
                                minFractionDigits={0}
                                onValueChange={(e) => formik.setFieldValue("property_suites", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_posicao_lote"
                            label="Posição do lote"
                            hasError={!!formik.errors.property_posicao_lote && formik.touched.property_posicao_lote}
                            error={formik.errors.property_posicao_lote}
                        >
                            <Dropdown
                                value={formik.values.property_posicao_lote}
                                onChange={(e) => formik.setFieldValue("property_posicao_lote", e.value)}
                                options={[{ id: PropertyPositionLote.MeioQuadra, label: "Meio da quadra" }, { id: PropertyPositionLote.Esquina, label: "Esquina" }, { id: PropertyPositionLote.CulDeSac, label: "Cul-de-sac" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_posicao_lote && formik.touched.property_posicao_lote}
                            />
                        </FormField>

                        <FormField
                            id="property_face_solar"
                            label="Face solar"
                            hasError={!!formik.errors.property_face_solar && formik.touched.property_face_solar}
                            error={formik.errors.property_face_solar}
                        >
                            <Dropdown
                                value={formik.values.property_face_solar}
                                onChange={(e) => formik.setFieldValue("property_face_solar", e.value)}
                                options={[{ id: PropertyFaceSolar.FrenteNorte, label: "Frente norte" }, { id: PropertyFaceSolar.FrenteSul, label: "Frente sul" }, { id: PropertyFaceSolar.Leste, label: "Leste" }, { id: PropertyFaceSolar.Oeste, label: "Oeste" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_face_solar && formik.touched.property_face_solar}
                            />
                        </FormField>

                        <FormField
                            id="property_ruido"
                            label="Nível de ruído"
                            hasError={!!formik.errors.property_ruido && formik.touched.property_ruido}
                            error={formik.errors.property_ruido}
                        >
                            <Dropdown
                                value={formik.values.property_ruido}
                                onChange={(e) => formik.setFieldValue("property_ruido", e.value)}
                                options={[{ id: PropertyNoise.Baixo, label: "Baixo" }, { id: PropertyNoise.Medio, label: "Médio" }, { id: PropertyNoise.Alto, label: "Alto" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_ruido && formik.touched.property_ruido}
                            />
                        </FormField>

                        <FormField
                            id="property_vista_casa"
                            label="Vista da casa"
                            hasError={!!formik.errors.property_vista_casa && formik.touched.property_vista_casa}
                            error={formik.errors.property_vista_casa}
                        >
                            <Dropdown
                                value={formik.values.property_vista_casa}
                                onChange={(e) => formik.setFieldValue("property_vista_casa", e.value)}
                                options={[{ id: PropertyVistaCasa.Nenhuma, label: "Nenhuma" }, { id: PropertyVistaCasa.Parcial, label: "Parcial" }, { id: PropertyVistaCasa.Aberta, label: "Aberta" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_vista_casa && formik.touched.property_vista_casa}
                            />
                        </FormField>

                        <FormField
                            id="property_condominio_fechado"
                            label="Condomínio fechado"
                            hasError={!!formik.errors.property_condominio_fechado && formik.touched.property_condominio_fechado}
                            error={formik.errors.property_condominio_fechado}
                        >
                            <InputSwitch
                                checked={formik.values.property_condominio_fechado ?? false}
                                onChange={(e) => formik.setFieldValue("property_condominio_fechado", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_vagas"
                            label="Vagas de garagem"
                            hasError={!!formik.errors.property_vagas && formik.touched.property_vagas}
                            error={formik.errors.property_vagas}
                        >
                            <InputNumber
                                invalid={!!formik.errors.property_vagas && formik.touched.property_vagas}
                                value={formik.values.property_vagas}
                                minFractionDigits={2}
                                onValueChange={(e) => formik.setFieldValue("property_vagas", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_facilidades"
                            label="Facilidades"
                            hasError={!!formik.errors.property_facilidades && formik.touched.property_facilidades}
                            error={formik.errors.property_facilidades}
                        >
                            <MultiSelect
                                value={formik.values.property_facilidades}
                                onChange={(e) => formik.setFieldValue("property_facilidades", e.value)}
                                options={[{ id: PropertyFacility.Piscina, label: "Piscina" }, { id: PropertyFacility.Edicula, label: "Edicula" }, { id: PropertyFacility.Gourmet, label: "Gourmet" }, { id: PropertyFacility.Escritorio, label: "Escritorio" }, { id: PropertyFacility.Lareira, label: "Lareira" }, { id: PropertyFacility.Solar, label: "Solar" }, { id: PropertyFacility.Cisterna, label: "Cisterna" }, { id: PropertyFacility.Cftv, label: "CFTV" }, { id: PropertyFacility.PortaoAuto, label: "Portão automático" }]}
                                optionLabel="label"
                                optionValue="id"
                                invalid={!!formik.errors.property_facilidades && formik.touched.property_facilidades}
                            />
                        </FormField>

                        <FormField
                            id="property_base_m2_construcao_local"
                            label="Mediana do m² de construção (comparáveis)"
                            hasError={!!formik.errors.property_base_m2_construcao_local && formik.touched.property_base_m2_construcao_local}
                            error={formik.errors.property_base_m2_construcao_local}
                        >
                            <InputNumber
                                invalid={!!formik.errors.property_base_m2_construcao_local && formik.touched.property_base_m2_construcao_local}
                                value={formik.values.property_base_m2_construcao_local}
                                minFractionDigits={2}
                                onValueChange={(e) => formik.setFieldValue("property_base_m2_construcao_local", e.value)}
                            />
                        </FormField>

                        <FormField
                            id="property_base_m2_terreno_local"
                            label="Mediana do m² de terreno (comparáveis)"
                            hasError={!!formik.errors.property_base_m2_terreno_local && formik.touched.property_base_m2_terreno_local}
                            error={formik.errors.property_base_m2_terreno_local}
                        >
                            <InputNumber
                                invalid={!!formik.errors.property_base_m2_terreno_local && formik.touched.property_base_m2_terreno_local}
                                value={formik.values.property_base_m2_terreno_local}
                                minFractionDigits={2}
                                onValueChange={(e) => formik.setFieldValue("property_base_m2_terreno_local", e.value)}
                            />
                        </FormField>
                    </View>
                        </AccordionTab>
                        )}

                        <AccordionTab header="Data e endereço">
                    <View flexColumn gap={2} className="pt-2">
                    <FormField
                        id="purchaseDate"
                        label="Data de compra"
                        hasError={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                        error={formik.errors.purchaseDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                            value={formik.values.purchaseDate}
                            dateFormat="dd/mm/yy"
                            readOnlyInput
                            showIcon
                            minDate={ASSET_MIN_PURCHASE_DATE}
                            disabledDays={[0, 6]}
                            onChange={(event) => {
                                formik.setFieldValue("purchaseDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <div className="grid gap-3">
                        <div className="col-12 md:col-6">
                    <FormField
                        id="cep"
                        hasError={!!formik.errors.cep && formik.touched.cep}
                        error={formik.errors.cep}
                        label="CEP"
                    >
                        <div className="p-inputgroup flex-1">
                            <InputText
                                invalid={!!formik.errors.cep && formik.touched.cep}
                                value={formik.values.cep}
                                onChange={(e) =>
                                    formik.setFieldValue("cep", e.target.value)
                                }
                            />

                            <span className="p-inputgroup-addon">
                                {
                                    cepQuery.loadingCep && (
                                        <i className="pi pi-spin pi-spinner"></i>
                                    )
                                }

                                {
                                    !cepQuery.loadingCep && (
                                        <i className="pi pi-search"></i>
                                    )
                                }
                            </span>
                        </div>
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6">
                    <FormField
                        id="uf"
                        hasError={!!formik.errors.uf && formik.touched.uf}
                        error={formik.errors.uf}
                        label="Estado (UF)"
                    >
                        <InputText
                            invalid={!!formik.errors.uf && formik.touched.uf}
                            value={formik.values.uf}
                            onChange={(e) =>
                                formik.setFieldValue("uf", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6">
                    <FormField
                        id="cidade"
                        hasError={!!formik.errors.localidade && formik.touched.localidade}
                        error={formik.errors.localidade}
                        label="Cidade"
                    >
                        <InputText
                            invalid={!!formik.errors.localidade && formik.touched.localidade}
                            value={formik.values.localidade}
                            onChange={(e) =>
                                formik.setFieldValue("localidade", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6">
                    <FormField
                        id="bairro"
                        hasError={!!formik.errors.bairro && formik.touched.bairro}
                        error={formik.errors.bairro}
                        label="Bairro"
                    >
                        <InputText
                            invalid={!!formik.errors.bairro && formik.touched.bairro}
                            value={formik.values.bairro}
                            onChange={(e) =>
                                formik.setFieldValue("bairro", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                        <div className="col-12">
                    <FormField
                        id="logradouro"
                        hasError={!!formik.errors.logradouro && formik.touched.logradouro}
                        error={formik.errors.logradouro}
                        label="Logradouro"
                    >
                        <InputText
                            invalid={!!formik.errors.logradouro && formik.touched.logradouro}
                            value={formik.values.logradouro}
                            onChange={(e) =>
                                formik.setFieldValue("logradouro", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-4">
                    <FormField
                        id="numero"
                        hasError={!!formik.errors.number && formik.touched.number}
                        error={formik.errors.number}
                        label="Número"
                    >
                        <InputText
                            invalid={!!formik.errors.number && formik.touched.number}
                            value={formik.values.number}
                            onChange={(e) =>
                                formik.setFieldValue("number", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-8">
                    <FormField
                        id="complement"
                        hasError={!!formik.errors.complement && formik.touched.complement}
                        error={formik.errors.complement}
                        label="Complemento"
                    >
                        <InputText
                            invalid={!!formik.errors.complement && formik.touched.complement}
                            value={formik.values.complement}
                            onChange={(e) =>
                                formik.setFieldValue("complement", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                    </div>
                    </View>
                        </AccordionTab>

                        <AccordionTab header="Metragem e cômodos">
                    <View flexColumn gap={2} className="pt-2">
                    <div className="grid gap-3">
                        <div className="col-12 md:col-6 lg:col-4">
                    <FormField
                        id="sizeM2"
                        hasError={!!formik.errors.sizeM2 && formik.touched.sizeM2}
                        error={formik.errors.sizeM2}
                        label="Metragem"
                    >
                        <InputNumber
                            invalid={!!formik.errors.sizeM2 && formik.touched.sizeM2}
                            value={formik.values.sizeM2}
                            onValueChange={(e) =>
                                formik.setFieldValue("sizeM2", e.target.value)
                            }
                            minFractionDigits={2}
                            maxFractionDigits={5}
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                    <FormField
                        id="bedrooms"
                        hasError={!!formik.errors.bedrooms && formik.touched.bedrooms}
                        error={formik.errors.bedrooms}
                        label="Quantos quartos?"
                    >
                        <InputNumber
                            invalid={!!formik.errors.bedrooms && formik.touched.bedrooms}
                            value={formik.values.bedrooms}
                            onValueChange={(e) =>
                                formik.setFieldValue("bedrooms", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                    <FormField
                        id="suits"
                        hasError={!!formik.errors.suits && formik.touched.suits}
                        error={formik.errors.suits}
                        label="Quantas suits?"
                    >
                        <InputNumber
                            invalid={!!formik.errors.suits && formik.touched.suits}
                            value={formik.values.suits}
                            onValueChange={(e) =>
                                formik.setFieldValue("suits", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                    <FormField
                        id="bathrooms"
                        hasError={!!formik.errors.bathrooms && formik.touched.bathrooms}
                        error={formik.errors.bathrooms}
                        label="Quantos banheiros?"
                    >
                        <InputNumber
                            invalid={!!formik.errors.bathrooms && formik.touched.bathrooms}
                            value={formik.values.bathrooms}
                            onValueChange={(e) =>
                                formik.setFieldValue("bathrooms", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                    <FormField
                        id="garages"
                        hasError={!!formik.errors.garages && formik.touched.garages}
                        error={formik.errors.garages}
                        label="Quantas vagas de garagem?"
                    >
                        <InputNumber
                            invalid={!!formik.errors.garages && formik.touched.garages}
                            value={formik.values.garages}
                            onValueChange={(e) =>
                                formik.setFieldValue("garages", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                    </div>
                    </View>
                        </AccordionTab>

                        <AccordionTab header="Valores e identificação">
                    <View flexColumn gap={2} className="pt-2">
                    <div className="grid gap-3">
                        <div className="col-12 md:col-6">
                    <FormField
                        id="amount"
                        hasError={!!formik.errors.amount && formik.touched.amount}
                        error={formik.errors.amount}
                        label="Valor de compra"
                    >
                        <InputNumber
                            invalid={!!formik.errors.amount && formik.touched.amount}
                            value={formik.values.amount}
                            onValueChange={(e) =>
                                formik.setFieldValue("amount", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6">
                    <FormField
                        id="property_actual_value"
                        label="Valor atual do imóvel"
                        hasError={!!formik.errors.property_actual_value && formik.touched.property_actual_value}
                        error={formik.errors.property_actual_value}
                    >
                        <InputNumber
                            invalid={!!formik.errors.property_actual_value && formik.touched.property_actual_value}
                            value={formik.values.property_actual_value}
                            onValueChange={(e) => formik.setFieldValue("property_actual_value", e.value)}
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6">
                    <FormField
                        id="description"
                        hasError={!!formik.errors.description && formik.touched.description}
                        error={formik.errors.description}
                        label="Apelido"
                    >
                        <InputText
                            invalid={!!formik.errors.description && formik.touched.description}
                            value={formik.values.description}
                            onChange={(e) =>
                                formik.setFieldValue("description", e.target.value)
                            }
                        />
                    </FormField>
                        </div>
                        <div className="col-12 md:col-6">
                    <FormField
                        id="yearConstruction"
                        hasError={!!formik.errors.yearConstruction && formik.touched.yearConstruction}
                        error={formik.errors.yearConstruction}
                        label="Ano de construção"
                    >
                        <InputMask
                            invalid={!!formik.errors.yearConstruction && formik.touched.yearConstruction}
                            value={formik.values.yearConstruction}
                            unmask
                            onChange={(e) =>
                                formik.setFieldValue("yearConstruction", e.target.value)
                            }
                            mask="9999"
                        />
                    </FormField>
                        </div>
                    </div>
                    </View>
                        </AccordionTab>
                    </Accordion>
                    </Container>

                    <View fullWidth>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                return Promise.resolve();
                            }}
                            className="w-full justify-content-center"
                            loading={propertyInsertMutation.isPending || propertyUpdateMutation.isPending}
                            disabled={propertyInsertMutation.isPending || propertyUpdateMutation.isPending}
                        >
                            {isEdit ? "Editar" : "Cadastrar"}
                        </Button>
                    </View>
                </View>
            </Show>
        </>
    );
}

export default PropertyHandleForm;
